import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Activity } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import ChatThread from '../components/chat/ChatThread';
import ChatInput from '../components/chat/ChatInput';
import PreAuthHero from '../components/chat/PreAuthHero';
import chatApi from '../services/chatApi';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

/**
 * The new home screen. Pre-auth: welcome + login/signup cards.
 * Post-auth: wrapped in DashboardLayout so sidebar stays for power users.
 *
 * Key flows handled locally:
 *   - local_advance     : signup step progression
 *   - auth_success      : login/signup/reset completion
 *   - forgot_sent       : confirmation
 *   - send              : POST /api/chat/send with chip action or text
 */
const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [hasEarlier, setHasEarlier] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [params] = useSearchParams();
  const resetToken = params.get('reset_token');
  // Deep-link intent (e.g. /chat?intent=nutrition_baseline) — coming from a
  // pillar page CTA. After load, fire the intent so the right card renders.
  const deepIntent = params.get('intent');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Authed users get today's history first (so chat persists across logins);
      // bootstrap fires only when there's nothing logged today so we don't
      // re-greet someone who's been chatting for hours.
      let messages = null;
      if (user) {
        try {
          // Anchor "today" to the user's LOCAL midnight (not UTC), so an
          // evening message doesn't bleed into tomorrow's session for users
          // off UTC. setHours(0,0,0,0) → local midnight; toISOString() → the
          // correct UTC instant for the backend's UTC-stored created_at.
          const localMidnight = new Date();
          localMidnight.setHours(0, 0, 0, 0);
          // Option B: open on just the last couple of messages from today for a
          // clean, fresh feel — the user scrolls UP to lazy-load earlier ones
          // (ChatThread → onLoadEarlier). Avoids dumping the whole day on open.
          const h = await chatApi.history({ since_ts: localMidnight.toISOString(), limit: 2 });
          const hist = h.data?.messages || [];
          if (hist.length > 0) {
            messages = hist;
          }
        } catch (e) {
          // history endpoint failure is non-fatal — fall through to bootstrap
        }
      }
      if (!messages) {
        const res = await chatApi.bootstrap();
        messages = res.data.messages || [];
      }
      setMessages(messages);
      setHasEarlier(true);
    } catch (e) {
      console.error('Chat load failed', e);
      toast.error('Could not load chat');
    } finally {
      setLoading(false);
    }
    // Reload only when the user identity (id) changes — NOT on every auth-context
    // re-render. Depending on the whole `user` object caused repeated re-fetches
    // (thread flicker / "breaking").
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Fetch the page of messages older than the current earliest, prepend them.
  const loadEarlier = useCallback(async () => {
    if (loadingEarlier || !messages.length) return;
    const earliest = messages.find(m => m.created_at)?.created_at;
    if (!earliest) { setHasEarlier(false); return; }
    setLoadingEarlier(true);
    try {
      const res = await chatApi.history({ before: earliest, limit: 50 });
      const older = res.data?.messages || [];
      if (older.length) setMessages(prev => [...older, ...prev]);
      if (older.length < 50) setHasEarlier(false);
    } catch (e) {
      toast.error('Could not load earlier messages');
    } finally {
      setLoadingEarlier(false);
    }
  }, [loadingEarlier, messages]);

  useEffect(() => {
    // Wait for AuthContext to finish initial load so bootstrap sees the token
    if (authLoading) return;
    // If landing with a reset_token, show ONLY the reset card (don't send bootstrap text)
    if (resetToken) {
      setMessages([{
        id: 'reset-bootstrap',
        role: 'forma',
        text: 'Set a new password and you\'re in.',
        cards: [{ type: 'password_reset', data: { token: resetToken } }],
        chips: [],
      }]);
      setLoading(false);
      return;
    }
    load();
  }, [authLoading, resetToken, load, user?.id]);

  // Auto-fire deep-link intent ONCE after load completes.
  const deepIntentFired = React.useRef(false);
  useEffect(() => {
    if (loading || !user || !deepIntent || deepIntentFired.current) return;
    deepIntentFired.current = true;
    (async () => {
      try {
        const res = await chatApi.sendChip({ type: 'send', payload: { intent: deepIntent } });
        appendForma(res.data?.forma_messages || []);
      } catch {
        toast.error('Could not open that — try again from chat.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, deepIntent]);

  const appendUserText = (text) => {
    setMessages(prev => [...prev, { id: `local-u-${Date.now()}`, role: 'user', text, cards: [], chips: [] }]);
  };

  const appendForma = (msgs) => {
    setMessages(prev => [...prev, ...(msgs || []).map(m => ({ ...m, chips: m.chips || [], cards: m.cards || [] }))]);
  };

  const handleAction = async (action) => {
    if (!action) return;
    const { type, payload, label } = action;

    // Local-only: signup step advance
    if (type === 'local_advance') {
      if (payload?.user_text) appendUserText(payload.user_text);
      appendForma([{ id: `local-f-${Date.now()}`, role: 'forma', text: _nextStepPrompt(payload?.next_card?.data?.step), cards: [payload.next_card], chips: [] }]);
      return;
    }

    // Local-only: auth success — send the user straight to their Home/Dashboard
    if (type === 'auth_success') {
      navigate('/dashboard');
      return;
    }

    if (type === 'forgot_sent') {
      appendForma([{ id: `local-f-${Date.now()}`, role: 'forma', text: 'Reset link sent if that email is on file. Check inbox + spam.', cards: [], chips: [] }]);
      return;
    }

    // Server-side dispatched: chip taps with send action
    if (type === 'send' || type === 'trigger_card') {
      if (label) appendUserText(label);
      setSending(true);
      try {
        const res = await chatApi.sendChip({ type: 'send', payload });
        const formaMsgs = res.data.forma_messages || [];
        appendForma(formaMsgs);
        // Keep the rest of the app (Focus card, Behaviour card, IQ rings, XP) in
        // sync with anything Nikki just changed (logged a workout, swapped focus…).
        window.dispatchEvent(new Event('wellness:refresh'));
      } catch (e) {
        toast.error('Message failed');
      } finally { setSending(false); }
      return;
    }
  };

  const handleSendText = async (text) => {
    appendUserText(text);
    setSending(true);
    try {
      const res = await chatApi.sendMessage(text);
      const formaMsgs = res.data.forma_messages || [];
      appendForma(formaMsgs);
      // Sync the dashboard/Focus/Behaviour/IQ surfaces with anything Nikki changed.
      window.dispatchEvent(new Event('wellness:refresh'));
    } catch (e) {
      toast.error('Send failed');
    } finally { setSending(false); }
  };

  // Pre-auth: show big hero until user taps a choice; then degrade to normal chat thread.
  if (!user && !authLoading) {
    // Determine if hero should be shown: only when the latest forma message is the
    // welcome one (i.e. user hasn't picked new/existing yet) and we're not in reset mode.
    const lastForma = [...messages].reverse().find(m => m.role === 'forma');
    const lastIsWelcome = !!lastForma && (lastForma.cards || []).some(c => c.type === 'welcome_hero');
    const showHero = !resetToken && lastIsWelcome && messages.length <= 1;

    const heroHeadline = (lastForma?.cards || []).find(c => c.type === 'welcome_hero')?.data?.headline;
    const heroTagline = (lastForma?.cards || []).find(c => c.type === 'welcome_hero')?.data?.tagline;

    const handleHeroChoice = (choice) => {
      // Reuse the standard send path so the chat continues from the hero seamlessly.
      handleAction({
        type: 'send',
        payload: { intent: choice === 'new' ? 'new' : 'existing' },
        label: choice === 'new' ? "I'm new" : 'I have an account',
      });
    };

    return (
      <div className="min-h-screen bg-[#09090B] flex flex-col" data-testid="chat-page-preauth">
        <header className="border-b border-zinc-800 px-4 md:px-8 pb-3 flex items-center justify-between relative z-10" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
              <Activity className="w-5 h-5 text-black" />
            </div>
            <span className="font-secondary font-bold tracking-tight text-lg">Revivo<span className="text-primary">&nbsp;IQ</span></span>
          </div>
        </header>
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Activity className="w-8 h-8 text-primary animate-pulse" />
          </div>
        ) : showHero ? (
          <PreAuthHero
            headline={heroHeadline}
            tagline={heroTagline}
            onChoice={handleHeroChoice}
          />
        ) : (
          <ChatThread messages={messages} onAction={handleAction} sending={sending} />
        )}
        {!showHero && <ChatInput onSend={handleSendText} disabled={sending || resetToken} />}
      </div>
    );
  }

  // Authenticated view — inside DashboardLayout (sidebar stays for power users)
  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-0px)] lg:h-screen" data-testid="chat-page-auth">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Activity className="w-10 h-10 text-primary animate-pulse" />
          </div>
        ) : (
          <ChatThread
            messages={messages}
            onAction={handleAction}
            onLoadEarlier={loadEarlier}
            hasEarlier={hasEarlier}
            loadingEarlier={loadingEarlier}
            sending={sending}
          />
        )}
        <ChatInput onSend={handleSendText} disabled={sending} />
      </div>
    </DashboardLayout>
  );
};

function _nextStepPrompt(step) {
  switch (step) {
    case 'email': return 'Nice to meet you. What\'s your email?';
    case 'password': return 'Create a password (8+ chars).';
    case 'goal': return 'One last thing — how are you feeling, anything you\'d like to work on?';
    default: return '';
  }
}

export default Chat;
